/**
 * Conversational Onboarding Page
 * --------------------------------
 * Chat-based onboarding flow with dynamic slot-filling.
 * Users can type freely and AI extracts structured data.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Button, ErrorBoundary, Spinner } from '../../components/common';
import { InfoTip } from '../../components/common/InfoTip';
import { type TooltipKey } from '../../tooltips/copy';
import { MarkdownTypewriter } from '../../components/common/MarkdownRenderer';
import { useAuth } from '../../contexts/AuthContext';
import {
  useConversationalOnboarding,
  ConversationalOnboardingProvider,
  type ChatMessage,
} from '../../contexts/ConversationalOnboardingContext';
import { type ExtractedSlot, uploadOnboardingResume } from '../../services/conversationalOnboardingService';
import { messageStyles } from '../../styles/style';
import { routes } from '../../lib/routes';
import { useBranding } from '../../contexts/BrandingContext';
import useIsDevice from '../../hooks/useIsDevice';
import { Send, Check, Attachment, Trash, RefreshDouble, WarningCircle, Microphone, Sparks, Clock, EditPencil, LightBulbOn, Xmark, InfoCircle } from 'iconoir-react';
import toast from 'react-hot-toast';
import { Toaster } from '../../components/common';
import { useVoiceInput } from '../../hooks/useVoiceInput';

// Helper to check if a slot has a valid filled value
const isSlotFilled = (slot: ExtractedSlot | undefined): boolean => {
  if (!slot || slot.value === null || slot.value === undefined) return false;
  if (Array.isArray(slot.value)) return slot.value.length > 0;
  return String(slot.value).trim() !== '';
};

// Track maximum progress to prevent regression (monotonic progress)
const STORAGE_KEY_MAX_PROGRESS = 'onboarding_max_progress';

// Progress bar component
// UX fix: Calculate progress based on filled CORE + OPTIONAL slots, not just backend percent
// P1 FIX: Track max progress to prevent regression
const ProgressBar: React.FC<{
  percent: number;
  isComplete?: boolean;
  allSlots: Record<string, ExtractedSlot>;
  messageCount: number;
  userId?: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ percent, isComplete, allSlots, messageCount, userId }) => {
  // BUG-121 FIX: Use backend percent instead of local calculation
  // Removed local slot counting - allSlots and messageCount kept for API compatibility
  // Previous code calculated locally which caused 80% vs 100% mismatch
  // Backend's completion_percent is the source of truth
  // Keep monotonic progress (never decrease) for UX
  const storageKey = userId ? `${STORAGE_KEY_MAX_PROGRESS}_${userId}` : STORAGE_KEY_MAX_PROGRESS;
  const storedMax = React.useMemo(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? parseFloat(stored) : 0;
    } catch {
      return 0;
    }
  }, [storageKey]);

  // BUG-121 FIX: Use backend percent, not local calculation
  // Max of stored progress and backend percent (monotonic - never decrease)
  const currentMax = Math.max(storedMax, percent);

  // Store new max if higher
  React.useEffect(() => {
    if (currentMax > storedMax) {
      try {
        sessionStorage.setItem(storageKey, currentMax.toString());
      } catch {
        // Ignore storage errors
      }
    }
  }, [currentMax, storedMax, storageKey]);

  // If marked complete by backend, show 100%
  const displayPercent = isComplete ? 100 : Math.min(100, Math.max(0, currentMax));

  return (
    <div className='w-full bg-gray-200 rounded-full h-2 mb-4'>
      <div
        className='bg-primary-500 h-2 rounded-full transition-all duration-500 ease-out'
        style={{ width: `${displayPercent}%` }}
      />
      <p className='text-xs text-gray-500 mt-1 text-right inline-flex items-center gap-1 w-full justify-end'>
        {Math.round(displayPercent)}% complete
        <InfoTip tooltipKey='onboarding-progress' />
      </p>
    </div>
  );
};

// Suggestion chips component - DISABLED per UX audit
// Chips were confusing users - removed entirely to allow natural conversation flow
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SuggestionChips: React.FC<{
  suggestions: string[];
  onSelect: (suggestion: string) => void;
  disabled?: boolean;
}> = ({ suggestions, onSelect, disabled }) => {
  // UX audit: Remove suggestion chips entirely - they interrupt natural conversation
  return null;
};

// Helper function to format slot keys into readable labels
const formatSlotLabel = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to format slot values for display
const formatSlotValue = (value: any): string => {
  if (value === null || value === undefined) {
    return 'Not provided';
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'None';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    // BUG-004 FIX: Handle funding_need/check_size monetary ranges
    // Format {min: 5600000, max: 8400000} as "$5.6M - $8.4M"
    if (value.min !== undefined && value.max !== undefined) {
      const formatCurrency = (num: number): string => {
        if (num >= 1000000) {
          const millions = num / 1000000;
          return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
        }
        if (num >= 1000) {
          return `$${(num / 1000).toFixed(0)}K`;
        }
        return `$${num.toLocaleString()}`;
      };
      return `${formatCurrency(value.min)} - ${formatCurrency(value.max)}`;
    }
    // Fallback: stringify other objects
    return JSON.stringify(value);
  }
  return String(value);
};

// Priority slots to show at the top of the summary
const PRIORITY_SLOTS = [
  'name',
  'first_name',
  'full_name',
  'user_type',
  'role',
  'primary_goal',
  'goal',
  'industry',
  'industry_focus',
  'sector',
  'company',
  'company_name',
  'organization',
];

// Apr-24: slot-name → TooltipKey mapping so every review-row label can
// carry an InfoTip explaining the LLM-enum value the user never manually
// picked. Previously only `dealbreakers` had a tooltip — Pierre test showed
// users see `Primary Goal: Offer Services`, `Service Type: Finance/Accounting`
// etc. as jargon they can't interpret. Keys not in this map get no tooltip
// (safe default — free-form slots like `first_name` don't need one).
const SLOT_TOOLTIP_MAP: Record<string, TooltipKey> = {
  primary_goal: 'slot-primary-goal',
  user_type: 'slot-user-type',
  industry_focus: 'slot-industry-focus',
  geography: 'slot-geography',
  stage_preference: 'slot-stage-preference',
  engagement_style: 'slot-engagement-style',
  seeking_user_types: 'slot-seeking-user-types',
  requirements: 'slot-requirements',
  offerings: 'slot-offerings',
  dealbreakers: 'dealbreakers',
  service_type: 'slot-service-type',
  budget_range: 'slot-budget-range',
  role_type: 'slot-role-type',
  seniority_level: 'slot-seniority-level',
  remote_preference: 'slot-remote-preference',
  compensation_range: 'slot-compensation-range',
  skills_have: 'slot-skills-have',
  skills_need: 'slot-skills-need',
  commitment_level: 'slot-commitment-level',
  equity_expectations: 'slot-equity-expectations',
  check_size: 'slot-check-size',
  portfolio_size: 'slot-portfolio-size',
  investment_thesis: 'slot-investment-thesis',
  funding_need: 'slot-funding-need',
  funding_range: 'slot-funding-range',
  mentorship_areas: 'slot-mentorship-areas',
  mentorship_format: 'slot-mentorship-format',
  mentorship_commitment: 'slot-mentorship-commitment',
  achievement: 'slot-achievement',
  company_name: 'slot-company-name',
  company_stage: 'slot-company-stage',
  team_size: 'slot-team-size',
  experience_years: 'slot-experience-years',
  network_strength: 'slot-network-strength',
};

// Core slots that must be filled before allowing manual completion
// Matches backend SlotSchema.CORE_SLOTS
const CORE_SLOT_NAMES = [
  'primary_goal',
  'user_type',
  'industry_focus',
  'stage_preference',
  'geography',
];

// BUG-049 FIX: Role-aware optional slot suggestions
// BUG-078 FIX: Removed company_name, company - these are role-specific (founder/entrepreneur only)
// Universal slots that apply to everyone
const UNIVERSAL_OPTIONAL_SLOTS = [
  'experience_years',
  'role_title',
  'requirements',
  'engagement_style',
  'dealbreakers',
  'timeline',
];

// Role-specific optional slots (only shown to relevant user types)
// BUG-078 FIX: Added company_name/company to founder/entrepreneur, improved job seeker slots,
// added 'candidate' key for "Job Seeker/Candidate" user_type matching
const ROLE_SPECIFIC_OPTIONAL_SLOTS: Record<string, string[]> = {
  founder: ['funding_need', 'company_stage', 'traction_metrics', 'market_size', 'team_size', 'company_name', 'company'],
  entrepreneur: ['funding_need', 'company_stage', 'traction_metrics', 'market_size', 'team_size', 'company_name', 'company'],
  investor: ['check_size', 'investment_thesis', 'portfolio_focus'],
  angel_investor: ['check_size', 'investment_thesis', 'portfolio_focus'],
  vc_partner: ['check_size', 'investment_thesis', 'portfolio_focus', 'fund_size'],
  advisor: ['expertise_areas', 'mentorship_style', 'availability'],
  mentor: ['expertise_areas', 'mentorship_style', 'availability'],
  executive: ['leadership_experience', 'industry_expertise'],
  corporate_executive: ['leadership_experience', 'industry_expertise', 'corporate_initiatives'],
  // BUG-078 FIX: Updated job seeker slots to match JOB_SEARCH template (role_type, seniority_level, etc.)
  job_seeker: ['role_type', 'seniority_level', 'remote_preference', 'compensation_range', 'skills_have'],
  candidate: ['role_type', 'seniority_level', 'remote_preference', 'compensation_range', 'skills_have'],
  job_seeker_candidate: ['role_type', 'seniority_level', 'remote_preference', 'compensation_range', 'skills_have'],
  service_provider: ['service_offerings', 'client_focus', 'budget'],
};

// Helper to get optional slots based on user_type
function getOptionalSlotsForUser(userType: string | undefined): string[] {
  if (!userType) return UNIVERSAL_OPTIONAL_SLOTS;

  // Normalize user_type to match keys (lowercase, replace spaces/slashes with underscore)
  const roleKey = userType.toLowerCase().replace(/[\/\s]+/g, '_');

  // Try exact match first, then partial match
  let roleSlots = ROLE_SPECIFIC_OPTIONAL_SLOTS[roleKey];

  if (!roleSlots) {
    // Partial match: "Founder/Entrepreneur" -> try "founder"
    for (const key of Object.keys(ROLE_SPECIFIC_OPTIONAL_SLOTS)) {
      if (roleKey.includes(key) || key.includes(roleKey.split('_')[0])) {
        roleSlots = ROLE_SPECIFIC_OPTIONAL_SLOTS[key];
        break;
      }
    }
  }

  return [...UNIVERSAL_OPTIONAL_SLOTS, ...(roleSlots || [])];
}

// Legacy constant for backward compatibility (uses universal slots)
const OPTIONAL_SLOT_NAMES = UNIVERSAL_OPTIONAL_SLOTS;

// Thresholds for showing "I'm ready" button
const MIN_CORE_SLOTS_FOR_READY = 4;      // Need 4 of 5 core slots
const MIN_OPTIONAL_SLOTS_FOR_READY = 2;  // Need at least 2 optional slots
const MIN_MESSAGES_FOR_READY = 4;        // Need at least 2 Q&A exchanges (4 messages total)

// Profile Processing Overlay Component
// Shows a full-screen overlay while the profile is being created
// Apr-24: copy updated to explain what's actually happening — users
// previously saw a generic "Creating your profile..." and then hard-cut to
// dashboard with no context about what was generated or where matches are.
const ProfileProcessingOverlay: React.FC = () => {
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm'>
      <div className='flex flex-col items-center gap-6 p-8 max-w-md'>
        <Spinner size='xl' color='primary' />
        <div className='text-center'>
          <h2 className='text-xl font-semibold text-gray-900 mb-2'>
            Generating your AI Summary &amp; matches…
          </h2>
          <p className='text-gray-500 text-sm leading-relaxed'>
            We're building your profile narrative, embedding it for matching, and running it
            against the user base. Takes about 60 seconds.
          </p>
          <p className='text-gray-400 text-xs mt-4'>
            You'll land on your dashboard, first matches should be waiting there.
          </p>
        </div>
      </div>
    </div>
  );
};

// Apr-24 iterations 2-4: the onboarding guidance lives in two light-
// touch surfaces only — (B) a "How this works" button top-right that opens
// a modal with the full orientation + example, and (C) a ResumeUploadNudge
// above the chat input that calls out the already-existing paperclip. The
// AI's first message renders as-is from the backend — iteration 2 tried
// inlining framing into it, iteration 4 reverted because the message got
// too long and duplicated the modal content. Keeps the chat short + clean;
// users who want framing click the header button, users who don't see a
// quick welcome + question.

// HowThisWorksModal — opened from the header button. Contains the detailed
// framing (time, format, AI-summary review) plus the example seed answer
// for users who want a concrete reference. Non-intrusive — hidden by default,
// user-initiated.
const HowThisWorksModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col animate-in fade-in zoom-in duration-200'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3'>
          <div className='inline-flex items-center gap-2'>
            <Sparks className='w-5 h-5 text-primary-500' />
            <h2 className='text-xl font-semibold text-heading-500'>How onboarding works</h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors -m-1 p-1'
            aria-label='Close'
          >
            <Xmark className='w-5 h-5' />
          </button>
        </div>

        {/* Body */}
        <div className='px-6 py-5 overflow-y-auto'>
          <ul className='space-y-3 text-sm text-body-500'>
            <li className='flex items-start gap-2'>
              <Clock className='w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5' />
              <span>
                <strong className='text-heading-500'>About 5–8 minutes.</strong> You chat with our
                AI, no forms, no dropdowns, just a conversation. Richer answers take a bit longer but produce sharper matches.
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <LightBulbOn className='w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5' />
              <span>
                <strong className='text-heading-500'>Be specific.</strong> Mention what you're
                building, what you need from connections, and what you can offer. Richer answers
                get sharper matches.
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <InfoCircle className='w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5' />
              <span>
                <strong className='text-heading-500'>Shared with your matches.</strong> The profile we
                build from this chat, and the AI's take on why you're a fit, are shown to the people
                you match with. Your raw conversation stays private, so write as you'd want a new
                connection to read it.
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <EditPencil className='w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5' />
              <span>
                <strong className='text-heading-500'>
                  You'll review an AI Summary at the end.
                </strong>{' '}
                Tap any field to correct it before we save, nothing ships to matching without your
                sign-off.
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <Attachment className='w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5' />
              <span>
                <strong className='text-heading-500'>
                  Upload a resume or LinkedIn PDF (optional).
                </strong>{' '}
                Resumes sharpen every part of your persona, not just experience. On LinkedIn: Me
                → More → Save to PDF, then drop the file via the paperclip.
              </span>
            </li>
          </ul>

          <div className='mt-5 rounded-lg bg-gray-50 border border-gray-100 p-4'>
            <p className='text-xs font-medium text-heading-500 mb-2'>Example answer</p>
            <p className='text-xs text-body-500 leading-relaxed italic'>
              "Hey! I'm Alex, solo founder on Stackline, a B2B SaaS tool that automates invoice
              reconciliation for accounting firms. Launched 6 months back, 14 paying firms, $18k
              MRR. Biggest need right now is seed investors with B2B/fintech experience who can
              write a first check into my round. Happy to share my operator network of 80+
              accounting-firm partners with anyone building adjacent tools."
            </p>
            <p className='mt-2 text-[11px] text-gray-400'>
              Your answer doesn't need to be this polished, just specific and honest.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className='px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl'>
          <Button color='primary' size='md' onClick={onClose} className='w-full'>
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

// Apr-24 iteration 3: resume-upload nudge. The paperclip button inside
// the ChatInput already accepts PDF + Word uploads and feeds them into
// persona generation via `combine_user_data(questions, resume_text,
// conversation_text)` — the persona prompt explicitly says "USE BOTH SOURCES
// FOR ALL FIELDS". But there's zero discoverability: icon is unlabeled and
// nothing in the flow mentions resumes improve match quality. This nudge
// sits above the chat input, visible only in the first few messages, and
// explicitly calls attention to the already-existing paperclip. Dismissable
// per user; auto-hides after 3 messages (user has committed to the flow).
const STORAGE_KEY_RESUME_NUDGE_DISMISSED = 'onboarding_resume_nudge_dismissed';
const ResumeUploadNudge: React.FC<{ userId?: string; messageCount: number }> = ({
  userId,
  messageCount,
}) => {
  const storageKey = userId
    ? `${STORAGE_KEY_RESUME_NUDGE_DISMISSED}_${userId}`
    : STORAGE_KEY_RESUME_NUDGE_DISMISSED;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Auto-hide once the user has committed to the flow (3+ messages = at least
  // one user turn in most cases). Avoids the nudge feeling stale mid-flow.
  if (dismissed || messageCount > 3) return null;

  return (
    <div className='mb-3 flex items-center justify-between gap-3 rounded-lg bg-primary-50/60 border border-primary-100 px-4 py-2.5'>
      <div className='flex items-center gap-2 text-xs text-body-500 leading-relaxed'>
        <Attachment className='w-4 h-4 text-primary-500 flex-shrink-0' />
        <span>
          <strong className='text-heading-500'>Got a resume or LinkedIn PDF?</strong> Upload for
          much richer persona context, tap the paperclip below.
        </span>
      </div>
      <button
        type='button'
        onClick={handleDismiss}
        className='text-gray-400 hover:text-gray-600 flex-shrink-0 -mr-1.5 flex items-center justify-center h-9 w-9'
        aria-label='Dismiss resume upload nudge'
      >
        <Xmark className='w-3.5 h-3.5' />
      </button>
    </div>
  );
};

// HowThisWorksButton — fixed top-right position (mirror of the Exit button on
// top-left in OnboardingLayout). Uses the same pill styling for visual
// symmetry. Opens the detailed modal.
const HowThisWorksButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='fixed top-24 right-4 z-50 flex items-center gap-2 px-3 py-2.5 text-sm text-body-500 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors'
    title='How onboarding works'
    aria-label='How onboarding works'
  >
    <InfoCircle className='w-4 h-4' />
    How this works
  </button>
);

// Completion Summary Modal Component — Apr-23: ports mobile's tap-to-edit
// (mobile commit 490f413) to web so users can correct extracted slots before
// persona generation. Edits collect into a local `overrides` dict and ship to
// the backend via the complete() payload as `slot_overrides`; AI service
// merges them onto the extracted slots before Q&A conversion. Signature of
// `onConfirm` extended to pass the overrides dict through.
const CompletionSummaryModal: React.FC<{
  allSlots: Record<string, ExtractedSlot>;
  onConfirm: (overrides?: Record<string, string>) => void;
  isLoading?: boolean;
}> = ({ allSlots, onConfirm, isLoading }) => {
  // Sort slots: priority ones first, then alphabetically
  const sortedSlots = Object.entries(allSlots).sort(([keyA], [keyB]) => {
    const priorityA = PRIORITY_SLOTS.indexOf(keyA.toLowerCase());
    const priorityB = PRIORITY_SLOTS.indexOf(keyB.toLowerCase());

    if (priorityA !== -1 && priorityB !== -1) {
      return priorityA - priorityB;
    }
    if (priorityA !== -1) return -1;
    if (priorityB !== -1) return 1;
    return keyA.localeCompare(keyB);
  });

  // Filter out empty or low-confidence slots
  const displaySlots = sortedSlots.filter(([_, slot]) => {
    if (slot.value === null || slot.value === undefined) return false;
    if (Array.isArray(slot.value) && slot.value.length === 0) return false;
    if (typeof slot.value === 'string' && slot.value.trim() === '') return false;
    return slot.confidence >= 0.5; // Only show slots with reasonable confidence
  });

  // Edit state — local to the modal so navigating away discards edits.
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>('');

  const getDisplayValue = (key: string, slotValue: ExtractedSlot['value']): string => {
    if (key in overrides) return overrides[key];
    return formatSlotValue(slotValue);
  };

  const handleStartEdit = (key: string, currentValue: string) => {
    setEditingKey(key);
    setDraftValue(currentValue);
  };

  const handleSaveEdit = () => {
    if (!editingKey) return;
    const trimmed = draftValue.trim();
    if (trimmed) {
      setOverrides(prev => ({ ...prev, [editingKey]: trimmed }));
    } else {
      // Empty save discards the override
      setOverrides(prev => {
        const next = { ...prev };
        delete next[editingKey];
        return next;
      });
    }
    setEditingKey(null);
    setDraftValue('');
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setDraftValue('');
  };

  const handleConfirmClick = () => {
    onConfirm(Object.keys(overrides).length > 0 ? overrides : undefined);
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'>
      <div className='bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col animate-in fade-in zoom-in duration-200'>
        {/* Header */}
        {/* Apr-24: header copy now explains the AI Summary concept the
            user is about to see, not just "what we learned". Users previously
            met the term "AI Summary" for the first time here with no framing. */}
        <div className='px-6 py-5 border-b border-gray-100'>
          <div className='inline-flex items-center gap-1.5 mb-1'>
            <h2 className='text-xl font-semibold text-gray-900'>
              Your AI Summary
            </h2>
            <InfoTip tooltipKey='ai-summary-preview' />
          </div>
          <p className='text-sm text-gray-500 leading-relaxed'>
            Here's what we extracted from our conversation. This becomes your profile, the
            matching system reads it to find people for you.{' '}
            <strong className='text-gray-700'>Tap any field to correct it</strong> before we save.
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {displaySlots.length > 0 ? (
            <div className='space-y-3'>
              {displaySlots.map(([key, slot]) => {
                const isEditing = editingKey === key;
                const displayValue = getDisplayValue(key, slot.value);
                return (
                  <div
                    key={key}
                    className='py-2 border-b border-gray-50 last:border-0'
                  >
                    {isEditing ? (
                      <div className='flex flex-col gap-2'>
                        <span className='text-sm font-medium text-gray-600'>
                          {formatSlotLabel(key)}
                        </span>
                        <textarea
                          value={draftValue}
                          onChange={(e) => setDraftValue(e.target.value)}
                          autoFocus
                          rows={2}
                          className='w-full text-sm text-gray-900 border border-primary-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none'
                          aria-label={`Edit ${formatSlotLabel(key)}`}
                        />
                        <div className='flex gap-2 justify-end'>
                          <button
                            type='button'
                            onClick={handleCancelEdit}
                            className='px-3 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50'
                          >
                            Cancel
                          </button>
                          <button
                            type='button'
                            onClick={handleSaveEdit}
                            className='px-3 py-1 text-xs font-semibold text-white bg-primary-600 rounded-md hover:bg-primary-700'
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type='button'
                        onClick={() => handleStartEdit(key, displayValue)}
                        className='w-full flex justify-between items-start text-left hover:bg-gray-50 rounded -mx-1 px-1 py-1 transition-colors'
                        aria-label={`Edit ${formatSlotLabel(key)}. Current value: ${displayValue}`}
                      >
                        <div className='flex flex-col min-w-[120px]'>
                          <span className='text-sm font-medium text-gray-600 inline-flex items-center gap-1'>
                            {formatSlotLabel(key)}
                            {/* Apr-24: per-slot InfoTip on every row (was
                                dealbreakers-only). SLOT_TOOLTIP_MAP resolves the
                                slot name to a tooltip key; rows without a mapping
                                (e.g. first_name) render no tooltip. The stopPropagation
                                prevents the InfoTip trigger from also firing the
                                tap-to-edit button. */}
                            {SLOT_TOOLTIP_MAP[key] && (
                              <span onClick={(e) => e.stopPropagation()}>
                                <InfoTip tooltipKey={SLOT_TOOLTIP_MAP[key]} />
                              </span>
                            )}
                          </span>
                          <span className='text-xs text-primary-500 mt-0.5'>
                            Tap to edit
                          </span>
                        </div>
                        <span className='text-sm text-gray-900 text-right flex-1 ml-4'>
                          {displayValue}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className='text-gray-500 text-center py-8'>
              No profile information collected yet.
            </p>
          )}

          {/* May-01 M3 A1 (web parallel of mobile commit 292c2e6):
              clarifying note that the rich AI Summary narrative (the
              persona text the matching engine actually uses) is generated
              AFTER the user clicks "Looks good" — so it isn't available
              to display here, but lives on Settings → AI Summary where
              it can be viewed and edited anytime. Sets accurate
              expectations + tells the user where to find the canonical
              AI Summary. Same pattern as mobile preview.tsx. */}
          {displaySlots.length > 0 && (
            <div className='mt-4 flex items-start gap-3 rounded-md bg-primary-50 border-l-4 border-primary-500 px-4 py-3'>
              <Sparks className='w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-primary-700 mb-1'>
                  Your full AI Summary is coming next
                </p>
                <p className='text-sm text-gray-700 leading-relaxed'>
                  The fields above are quick-edit highlights. After you click "Looks good", we'll generate your detailed AI Summary — a richer narrative that drives matching — and show it on the next screen so you can review and edit before matching kicks off. You can also revisit it anytime from Settings → AI Summary.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl'>
          {/* Apr-24: explicit pre-confirm handoff so users know what
              clicking this button actually does. Previously opaque — user
              tapped and got dropped on the dashboard. */}
          <p className='text-xs text-gray-500 text-center mb-3 leading-relaxed'>
            Next: we'll generate your AI Summary, embed it for matching, and surface your first
            batch of matches on the dashboard. Takes about 60 seconds.
          </p>
          <Button
            color='primary'
            size='lg'
            onClick={handleConfirmClick}
            disabled={isLoading || editingKey !== null}
            className='w-full flex items-center justify-center gap-2'
          >
            {isLoading ? (
              <>
                <Spinner size='sm' color='white' />
                Creating your profile...
              </>
            ) : (
              <>
                <Check className='w-5 h-5' />
                Looks good, continue
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Chat message component
const ChatMessageBubble: React.FC<{
  message: ChatMessage;
  isLatest: boolean;
  userAvatar?: string;
  userName?: string;
  messageIndex?: number; // BUG-020 FIX: Track message position
}> = ({ message, isLatest, userAvatar, userName, messageIndex = 0 }) => {
  const { isMobile } = useIsDevice();
  const { favicon } = useBranding(); // V1.5.M10 B-P1: AI avatar follows the brand
  const isUser = message.role === 'user';
  // BUG-020 FIX: Don't typewrite the first AI message (welcome greeting)
  const isFirstAiMessage = !isUser && messageIndex === 0;

  return (
    <div className={`flex gap-3 mb-6 ${isUser ? 'flex-row-reverse' : ''}`}>
      <Avatar
        className='mt-1 flex-shrink-0'
        imageClassName={isUser ? '' : 'p-2'}
        size={isMobile ? 'md' : 'lg'}
        shape='square'
        src={isUser ? userAvatar : favicon}
        name={isUser ? userName : 'AI'}
      />
      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        {isUser ? (
          <div className={messageStyles.messageRightClassName}>
            {message.content}
          </div>
        ) : (
          <div className={messageStyles.messageLeftClassName}>
            {/* BUG-020 FIX: Typewrite latest message EXCEPT the first AI message (welcome greeting) */}
            {isLatest && !isFirstAiMessage ? (
              <MarkdownTypewriter
                speed={10}
                markdown={message.content}
                enable={true}
                className='text-base leading-[1.5rem] font-medium mb-0'
              />
            ) : (
              <div className='text-base leading-[1.5rem] font-medium'>
                {message.content}
              </div>
            )}
          </div>
        )}

{/* Slot extraction badges hidden - internal debugging info not shown to users */}
      </div>
    </div>
  );
};

// Chat input component with file upload
const ChatInput: React.FC<{
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  sessionId?: string;
  userId?: string;
}> = ({ onSend, disabled, placeholder = 'Type your message...', sessionId, userId }) => {
  const [input, setInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState<{ name: string; uploading: boolean } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isRecording, isTranscribing, toggleRecording } = useVoiceInput((text) => {
    setInput((prev) => prev ? `${prev} ${text}` : text);
  });

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    if (input.trim() && !disabled) {
      onSend(input.trim());
      setInput('');
      // height resets via the layout effect below when `input` clears
    }
  }, [input, disabled, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Auto-resize the textarea to fit its content, capped at 150px. Runs on mount
  // and on every value change (incl. clearing after send) via the layout effect
  // below, so the resting/cleared state sizes to fit the wrapped placeholder and
  // never clips it or shows a native scrollbar on narrow (mobile) widths.
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }, []);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    if (!sessionId || !userId) {
      toast.custom(t => (
        <Toaster message='Session not ready. Please try again.' type='error' id={t.id} />
      ));
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.custom(t => (
        <Toaster message='Only PDF and Word documents are allowed.' type='error' id={t.id} />
      ));
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.custom(t => (
        <Toaster message='File size must be under 5MB.' type='error' id={t.id} />
      ));
      return;
    }

    setUploadedFile({ name: file.name, uploading: true });

    try {
      // QA fix (Jun-29): upload via the BACKEND (authed; S3 + user_documents; forwards
      // to the AI service) using the shared api client. The previous direct
      // browser->AI-service fetch used VITE_AI_SERVICE_BASE_URL (unset in the prod
      // build -> localhost fallback) and carried no auth, so the paperclip silently
      // failed for everyone.
      const result = await uploadOnboardingResume(file, sessionId);
      if (result.code === 200 || result.code === 201) {
        setUploadedFile({ name: file.name, uploading: false });
        toast.custom(t => (
          <Toaster message={`Resume uploaded: ${file.name}`} type='success' id={t.id} />
        ));
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      setUploadedFile(null);
      toast.custom(t => (
        <Toaster message={`Failed to upload resume: ${error instanceof Error ? error.message : 'Unknown error'}`} type='error' id={t.id} />
      ));
    }
  }, [sessionId, userId]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFileUpload]);

  const removeUploadedFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-2'>
      {/* Uploaded file indicator */}
      {uploadedFile && (
        <div className='flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg'>
          <Attachment className='w-4 h-4 text-green-600' />
          <span className='text-sm text-green-700 flex-1 truncate'>
            {uploadedFile.uploading ? 'Uploading...' : uploadedFile.name}
          </span>
          {!uploadedFile.uploading && (
            <button
              type='button'
              onClick={removeUploadedFile}
              className='p-1 text-green-600 hover:text-red-500 transition-colors'
              title='Remove file'
            >
              <Trash className='w-4 h-4' />
            </button>
          )}
        </div>
      )}

      <div className='flex gap-2 items-center'>
        {/* Hidden file input */}
        <input
          type='file'
          ref={fileInputRef}
          className='hidden'
          accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          onChange={handleFileChange}
        />

        {/* Attachment button */}
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploadedFile?.uploading}
          className='h-12 w-12 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          title='Upload resume (PDF or Word)'
        >
          <Attachment className='w-5 h-5' />
        </button>

        <div className='flex-1 relative'>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className='w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl resize-none overflow-y-auto focus:border-primary-400 focus:outline-none disabled:opacity-50 disabled:bg-gray-50'
            style={{ minHeight: '48px', maxHeight: '150px' }}
          />
        </div>
        {/* Microphone button */}
        <button
          type='button'
          onClick={toggleRecording}
          disabled={disabled || isTranscribing}
          className={`h-12 w-12 flex items-center justify-center rounded-xl border-2 transition-colors ${
            isRecording
              ? 'border-red-400 bg-red-50 text-red-500 animate-pulse'
              : 'border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500'
          } disabled:opacity-50`}
          title={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Voice input'}
        >
          {isTranscribing ? (
            <div className='w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin' />
          ) : (
            <Microphone className='w-5 h-5' />
          )}
        </button>

        <Button
          type='submit'
          color='primary'
          disabled={disabled || !input.trim()}
          className='h-12 w-12 !p-0 flex items-center justify-center rounded-xl'
        >
          <Send className='w-5 h-5' />
        </Button>
      </div>
    </form>
  );
};

// Storage key to track if user just completed onboarding (prevents restart loop)
const STORAGE_KEY_JUST_COMPLETED = 'onboarding_just_completed';

// Main page content component
const ConversationalOnboardingContent: React.FC = () => {
  const { user } = useAuth();
  const { favicon, resolved: brandingResolved } = useBranding(); // V1.5.M10 B-P1: AI avatar follows the brand
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // State for showing completion summary modal
  const [showCompletionSummary, setShowCompletionSummary] = useState(false);
  const [summaryConfirmed, setSummaryConfirmed] = useState(false);
  // Apr-23: slot overrides captured from inline edits on the summary modal.
  // Shipped to AI service via completeOnboarding(overrides) → merged onto the
  // extracted slot_summary before persona generation.
  const [slotOverrides, setSlotOverrides] = useState<Record<string, string> | undefined>(undefined);

  // BUG-002 FIX: Track when "I'm ready" button has been clicked
  // This prevents multiple "done" messages from being sent
  const [isReadyClicked, setIsReadyClicked] = useState(false);
  const readyClickedRef = useRef(false);  // Ref for immediate check (no render delay)

  // May-05: forward-declared ref so the auto-fire useEffect (which runs
  // before handleComplete is declared in source order) can call into the
  // latest handleComplete via .current. Updated in an effect right after
  // handleComplete's useCallback. Avoids hoisting / TDZ issues + avoids
  // putting handleComplete in the auto-fire effect's deps (which would
  // cause re-fires if handleComplete's identity changed).
  const handleCompleteRef = useRef<(() => Promise<void>) | null>(null);

  // Apr-24 iteration 2: "How this works" modal toggle
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const {
    sessionId,
    isLoading,
    isSending,
    isProcessingProfile,
    messages,
    completionPercent,
    isComplete,
    suggestedQuestions,
    allSlots,
    sendError,
    lastFailedMessage,
    completionLocked,
    startSession,
    sendMessage,
    completeOnboarding,
    lockCompletion,
    retryLastMessage,
    clearError,
    reset,
  } = useConversationalOnboarding();

  // CRITICAL FIX: Redirect already-onboarded users to dashboard
  // This prevents the reset bug where completing onboarding creates a new session
  useEffect(() => {
    // Check if user just completed onboarding (flag set during completion)
    // IMPORTANT: Do NOT remove the flag here - it prevents race conditions
    // when navigation bounces back due to cached user.onboarding_status
    // BUG-004 FIX: Use user-scoped key to match what handleComplete sets
    const justCompletedKey = user?.id ? `onboarding_just_completed_${user.id}` : STORAGE_KEY_JUST_COMPLETED;
    const justCompleted = sessionStorage.getItem(justCompletedKey);
    if (justCompleted) {
      // May-04: route post-completion users through the AI Summary review page
      // (M3 A3 destination per `05287e1`), not directly to dashboard. This effect
      // races with completeOnboarding's inline navigate; aligning the destination
      // makes both paths consistent and the user always lands on the AI Summary
      // review whether they came via inline nav or this useEffect.
      navigate(routes.onboardingAISummary, { replace: true });
      return;
    }

    // Revisit case: user already onboarded long ago, no in-flight completion.
    // Send them to dashboard, not back through the AI Summary review.
    if (user?.onboarding_status === 'completed') {
      navigate(routes.home, { replace: true });
      return;
    }
  }, [user, navigate]);

  // Start session on mount - only if not already onboarded
  useEffect(() => {
    // CRITICAL: Don't start a new session if we just completed onboarding
    // This prevents the race condition where startSession() fires before navigation
    // BUG-004 FIX: Use user-scoped key to match what handleComplete sets
    const justCompletedKey = user?.id ? `onboarding_just_completed_${user.id}` : STORAGE_KEY_JUST_COMPLETED;
    const justCompleted = sessionStorage.getItem(justCompletedKey);
    if (justCompleted) {
      // May-04: same fix as above — route through the AI Summary review page,
      // not direct to dashboard. Keeps M3 A3 flow intact.
      navigate(routes.onboardingAISummary, { replace: true });
      return;
    }

    // Don't start if user is already onboarded
    if (user?.onboarding_status === 'completed') {
      return;
    }

    // QA fix (Jun-29): wait for the authoritative brand to resolve before starting, so
    // the greeting uses the correct organizer. a user got the default
    // "2Connect Professional" on a TokenizeThis link because start fired before
    // /branding/resolve returned. The startedRef guard ensures a single start.
    if (!brandingResolved) {
      return;
    }

    if (!sessionId && !isLoading) {
      startSession();
    }
  }, [sessionId, isLoading, startSession, user?.onboarding_status, navigate, brandingResolved]);

  // NOTE: Don't reset on unmount - state is persisted in sessionStorage
  // Reset happens only when onboarding is complete

  // Show completion summary modal when isComplete becomes true
  // May-05: collapsed completion flow to single AI Summary surface.
  // Pre-May-05 the user clicked "I'm ready" -> CompletionSummaryModal opened
  // (slot grid review) -> "Looks good" closed modal -> "Complete & View Matches"
  // button appeared -> click fired /complete -> route to AI Summary page.
  // That's TWO AI summaries (modal slot grid + AI Summary page). User pushback
  // on m3-proposal-preview.html intent: single AI Summary surface only.
  //
  // Post-May-05: I'm ready click -> API returns is_complete -> auto-fire
  // handleComplete -> /complete -> route to AI Summary. Slot edits move to
  // the AI Summary page (tap-to-edit per section flows through May-04
  // b4a273d override-persistence path back to onboarding_answers).
  useEffect(() => {
    if (isComplete && !completionLocked) {
      lockCompletion(); // prevent subsequent sendMessage state updates
      setSummaryConfirmed(true); // legacy state — kept set for any consumers we may have missed
      void handleCompleteRef.current?.();
    }
  }, [isComplete, completionLocked, lockCompletion]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: string) => {
    sendMessage(suggestion);
  }, [sendMessage]);

  // Handle "I'm ready" button - send completion message (lock happens after summary confirm)
  // BUG-002 FIX: Use ref for immediate check to prevent multiple clicks
  // BUG-028 FIX: DON'T lock completion here! Locking prevents the summary modal from showing.
  // The lock should only happen in handleSummaryConfirm AFTER user reviews the summary.
  const handleReadyClick = useCallback(() => {
    // Prevent multiple clicks using ref (immediate, no render delay)
    if (readyClickedRef.current) {
      return;
    }
    readyClickedRef.current = true;
    setIsReadyClicked(true);

    // Send completion message - API will return is_complete: true
    // This triggers the summary modal, then user confirms, THEN we lock and show complete button
    sendMessage("That's all, I'm done. Show me my matches.");
  }, [sendMessage]);

  // Handle summary confirmation - user reviewed and wants to continue
  // CRITICAL: Lock completion state to prevent regression from delayed API responses
  // Apr-23: accepts slot overrides from the review modal's inline edits and
  // stashes them in local state; handleComplete reads them on the follow-up
  // click that actually calls the API.
  const handleSummaryConfirm = useCallback((overrides?: Record<string, string>) => {
    setShowCompletionSummary(false);
    setSummaryConfirmed(true);
    if (overrides && Object.keys(overrides).length > 0) {
      setSlotOverrides(overrides);
    }
    lockCompletion(); // Prevent any further state updates from sendMessage
  }, [lockCompletion]);

  // Handle complete — fires /complete + routes to AI Summary page.
  // May-05: invoked from useEffect above (auto-fire on is_complete) instead
  // of from a manual button click. The pre-May-05 "Complete & View Matches"
  // button was removed when the slot-review modal was eliminated.
  // Note: completeOnboarding() in context handles everything:
  // - API call, error handling, toasts, and navigation to AI Summary page
  //   (per May-01 commit 05287e1: routes.onboardingAISummary, not dashboard).
  const handleComplete = useCallback(async () => {
    // Apr-24: clear onboarding-scoped storage keys before navigating so
    // a re-entry (should be blocked by the justCompleted flag but defense-in-
    // depth) doesn't show a stale 100% progress bar or pre-dismissed framing
    // card stuck in the "already seen" state on a fresh session.
    if (user?.id) {
      try {
        sessionStorage.removeItem(`${STORAGE_KEY_MAX_PROGRESS}_${user.id}`);
      } catch {
        /* ignore storage errors */
      }
    }
    await completeOnboarding(slotOverrides);
  }, [completeOnboarding, slotOverrides, user?.id]);

  // May-05: keep handleCompleteRef pointing at the latest handleComplete so
  // the auto-fire useEffect (declared earlier) can invoke it without taking
  // handleComplete as a dep.
  useEffect(() => {
    handleCompleteRef.current = handleComplete;
  }, [handleComplete]);

  if (isLoading && messages.length === 0) {
    return (
      <div className='flex flex-col justify-center items-center h-full my-12 px-6'>
        <div className='max-w-md text-center'>
          <h2 className='text-xl font-semibold text-foreground mb-3'>
            Let's find your ideal connections
          </h2>
          <p className='text-body-400 text-sm mb-6 leading-relaxed'>
            Our AI will have a quick conversation with you to understand what you're looking for.
            This takes about 5 minutes and helps us match you with the right people.
          </p>
          <Spinner size='lg' color='primary' />
          <p className='text-body-300 text-xs mt-3'>Starting your conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='relative w-full h-[calc(100dvh-5.55rem)] flex flex-col pb-4'>
      {/* Profile processing overlay - shows when creating profile */}
      {isProcessingProfile && <ProfileProcessingOverlay />}

      {/* Progress bar */}
      <div className='w-full max-w-[50.5rem] mx-auto px-4 sm:px-0 pt-4'>
        <ProgressBar
          percent={completionPercent}
          isComplete={isComplete}
          allSlots={allSlots}
          messageCount={messages.length}
          userId={user?.id}
        />
      </div>

      {/* Apr-24 iteration 2: "How this works" — button modal (option B).
          Visual symmetry with Exit button from OnboardingLayout (top-4 left-4). */}
      <HowThisWorksButton onClick={() => setShowHowItWorks(true)} />
      {showHowItWorks && <HowThisWorksModal onClose={() => setShowHowItWorks(false)} />}

      {/* Messages container */}
      <div className='grow overflow-y-auto w-full px-4 sm:px-0'>
        <div className='w-full max-w-[50.5rem] mx-auto'>
          {messages.map((message, index) => (
            <ChatMessageBubble
              key={message.id}
              message={message}
              isLatest={index === messages.length - 1 && message.role === 'assistant'}
              userAvatar={user?.avatar}
              userName={`${user?.first_name} ${user?.last_name}`}
              messageIndex={index}
            />
          ))}

          {/* Sending indicator */}
          {isSending && (
            <div className='flex items-center gap-2 mb-6'>
              <Avatar
                className='mt-1'
                imageClassName='p-2'
                size='lg'
                shape='square'
                src={favicon}
                name='AI'
              />
              <div className='flex items-center gap-2'>
                <Spinner size='sm' color='primary' />
                <span className='animate-pulse text-gray-500'>Thinking...</span>
              </div>
            </div>
          )}

          {/* Error state with retry button */}
          {sendError && !isSending && (
            <div className='flex items-start gap-3 mb-6 p-4 bg-red-50 border border-red-200 rounded-xl'>
              <WarningCircle className='w-5 h-5 text-red-500 flex-shrink-0 mt-0.5' />
              <div className='flex-1'>
                <p className='text-red-700 font-medium mb-1'>Something went wrong</p>
                <p className='text-red-600 text-sm mb-3'>{sendError}</p>
                <div className='flex gap-2'>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={retryLastMessage}
                    className='flex items-center gap-2'
                  >
                    <RefreshDouble className='w-4 h-4' />
                    Retry
                  </Button>
                  <Button
                    color='dark'
                    size='sm'
                    onClick={clearError}
                    className='flex items-center gap-2'
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* May-05: CompletionSummaryModal removed from active flow.
          Slot edits move to the AI Summary page (tap-to-edit per section
          flows through May-04 b4a273d override-persistence path). One AI
          Summary surface per m3-proposal-preview.html intent.
          The modal component itself is kept in this file for now (orphan
          cleanup deferred) but no longer rendered. */}

      {/* Input area */}
      <div className='w-full max-w-[50.5rem] mx-auto px-4 sm:px-0'>
        {/* May-05: removed the intermediate "Review locked in / Complete & View
            Matches" button block. The auto-fire useEffect on isComplete now
            calls handleComplete directly, eliminating the gap between
            CompletionSummaryModal-confirm and final completion. ProfileProcessing
            Overlay (rendered higher in this component) shows during the ~60s
            pipeline; user is then routed to OnboardingAISummaryPage where they
            review + edit the rich AI Summary. One AI Summary surface per
            m3-proposal-preview.html intent. */}

        {/* Manual complete button - only show at 100% completion */}
        {/* Button should NOT appear before 100% — incomplete profiles affect match quality */}
        {!isComplete && !completionLocked && !isReadyClicked && completionPercent >= 100 && (
          <div className='flex flex-col items-center mb-4 gap-3'>
            <div className='flex gap-3'>
              <Button
                color='primary'
                size='md'
                onClick={handleReadyClick}
                disabled={isSending || isLoading || isReadyClicked}
                className='flex items-center gap-2'
              >
                {isReadyClicked ? (
                  <>
                    <Spinner size='sm' color='white' />
                    Finding your matches...
                  </>
                ) : (
                  <>
                    <Check className='w-4 h-4' />
                    I'm ready - find my matches
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Suggestion chips */}
        {!isComplete && (
          <SuggestionChips
            suggestions={suggestedQuestions}
            onSelect={handleSuggestionSelect}
            disabled={isSending}
          />
        )}

        {/* Chat input */}
        {/* Apr-24 iteration 2: dropped the under-input "Chat naturally"
            reminder. The same guidance is now inline in the AI's first message
            (option A) — keeping both is redundant.
            Iteration 3: added ResumeUploadNudge above the input — calls out
            the already-existing paperclip upload (persona-prompt explicitly
            uses resume to ENRICH every field; users just didn't know). */}
        {!isComplete && (
          <>
            <ResumeUploadNudge userId={user?.id} messageCount={messages.length} />
            <ChatInput
              onSend={sendMessage}
              disabled={isSending || isLoading}
              placeholder='Tell me about yourself...'
              sessionId={sessionId ?? undefined}
              userId={user?.id}
            />
          </>
        )}
      </div>
    </div>
  );
};

// Wrapped page component with provider
const ConversationalOnboardingPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <ConversationalOnboardingProvider>
        <ConversationalOnboardingContent />
      </ConversationalOnboardingProvider>
    </ErrorBoundary>
  );
};

export default ConversationalOnboardingPage;
